import { useNavigate } from 'react-router-dom'
import { MapPin, TrendingUp, DollarSign, Clock } from 'lucide-react'
import { Button } from '../ui/Button'
import { useAppPath } from '../../hooks/useAppPath'
import { DstListing } from '../../services/api/admin'
import {
  formatCentsCompact,
  formatHoldPeriodMonths,
  formatReturnRange,
} from '../../lib/marketplaceFormatters'
import { SponsorMonogram } from './SponsorMonogram'

interface MarketplaceHeroProps {
  listing: DstListing
}

export function MarketplaceHero({ listing }: MarketplaceHeroProps) {
  const navigate = useNavigate()
  const appPath = useAppPath()
  const heroImage = listing.images[0] ?? null
  const location = [listing.addressCity, listing.addressState].filter(Boolean).join(', ')

  function handleViewDetails() {
    navigate(appPath(`/marketplace/${listing.id}`))
  }

  return (
    <div
      className="relative h-[300px] lg:h-[420px] w-full overflow-hidden rounded-2xl"
      aria-label={`Featured DST: ${listing.title}`}
    >
      {heroImage ? (
        <img
          src={heroImage}
          alt={listing.title}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <div className="absolute inset-0">
          <SponsorMonogram sponsor={listing.sponsor} />
        </div>
      )}

      <div
        className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10"
        aria-hidden="true"
      />

      <div className="relative h-full flex flex-col justify-end p-6 lg:p-10 max-w-2xl">
        <span className="inline-block mb-3 bg-white/20 backdrop-blur-sm text-white text-xs font-semibold uppercase tracking-widest rounded-full px-3 py-1 w-fit border border-white/30">
          Featured DST
        </span>
        <h2 className="text-3xl lg:text-5xl font-bold text-white leading-tight mb-2">
          {listing.title}
        </h2>
        {(listing.sponsor || location) && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-4">
            {listing.sponsor && (
              <span className="text-white/90 text-sm font-medium">{listing.sponsor}</span>
            )}
            {listing.dstAssetClass && (
              <span className="text-white/70 text-sm capitalize">{listing.dstAssetClass}</span>
            )}
            {location && (
              <span className="flex items-center gap-1 text-white/70 text-sm">
                <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                {location}
              </span>
            )}
          </div>
        )}
        <div className="flex flex-wrap gap-6 mb-5">
          <div>
            <p className="text-white/60 text-xs uppercase tracking-wide flex items-center gap-1 mb-0.5">
              <TrendingUp className="h-3 w-3" aria-hidden="true" />
              Target Return
            </p>
            <p className="text-white text-xl font-bold">
              {formatReturnRange(listing.dstExpectedReturnMin, listing.dstExpectedReturnMax)}
            </p>
          </div>
          <div>
            <p className="text-white/60 text-xs uppercase tracking-wide flex items-center gap-1 mb-0.5">
              <DollarSign className="h-3 w-3" aria-hidden="true" />
              Min Investment
            </p>
            <p className="text-white text-xl font-bold">
              {formatCentsCompact(listing.dstMinInvestmentCents)}
            </p>
          </div>
          <div>
            <p className="text-white/60 text-xs uppercase tracking-wide flex items-center gap-1 mb-0.5">
              <Clock className="h-3 w-3" aria-hidden="true" />
              Hold Period
            </p>
            <p className="text-white text-xl font-bold">
              {formatHoldPeriodMonths(listing.dstHoldPeriodMonths)}
            </p>
          </div>
        </div>
        <Button
          onClick={handleViewDetails}
          className="w-fit"
          aria-label={`View details for ${listing.title}`}
        >
          View details
        </Button>
      </div>
    </div>
  )
}
